/**
 * [locations_map] entry. The map app (react-map-gl + mapbox-gl, ~700KB) is
 * pulled in with a dynamic import() only once a .locations-map-container is
 * actually on the page, so this entry itself stays tiny. The standalone
 * search box has its own entry (search.tsx) and never touches Mapbox.
 *
 * Note: no stylesheet may be imported from the lazily-loaded App chunk — CSS
 * imported there would be injected into document.head by Vite instead of the
 * shadow root. map.css is imported here (entry-level) purely so Vite builds
 * it; it reaches the shadow root via locationsMapConfig.mapCssHref.
 */
import { createRoot } from "react-dom/client";
import { getLocationsMapConfig, onDomReady, prepareShadow } from "./lib/shadow";
import "./styles/map.css";

async function renderIntoShadow(
  host: Element,
  center?: [number, number],
  zoom?: number
) {
  const element = host as HTMLElement;
  // Ensure host has sensible default sizing if none provided via shortcode
  if (!element.style.height) {
    element.style.height = "852px";
  }
  if (!element.style.position) {
    element.style.position = "relative";
  }
  const shadow = prepareShadow(element, "mapCssHref");

  const mount = document.createElement("div");
  mount.style.width = "100%";
  mount.style.height = "100%";
  shadow.appendChild(mount);

  // react-map-gl kicks off import("mapbox-gl") when its module evaluates, so
  // deferring the App chunk defers Mapbox too.
  const { App } = await import("./components/App");
  createRoot(mount).render(<App center={center} zoom={zoom} />);
}

function initializeMaps() {
  // Find all map containers on the page — nothing to do (and nothing to
  // download) if there are none.
  const containers = document.querySelectorAll(".locations-map-container");
  if (containers.length === 0) {
    return;
  }

  const config = getLocationsMapConfig();

  if (!config) {
    console.error("Locations Map: Configuration not found");
    return;
  }

  if (!config.accessToken) {
    console.error("Locations Map: Mapbox access token is required");
    return;
  }

  // Parse URL params once
  const params = new URLSearchParams(window.location.search);
  const paramCenter = params.get("center");
  const urlCenter = paramCenter
    ? (paramCenter.split(",").map((v) => parseFloat(v)) as [number, number])
    : undefined;
  const lat = params.get("lat");
  const lng = params.get("lng");
  const urlCenter2 =
    lat && lng
      ? ([parseFloat(lng), parseFloat(lat)] as [number, number])
      : undefined;
  const urlZoom = params.get("zoom")
    ? parseFloat(params.get("zoom") as string)
    : undefined;

  containers.forEach((container) => {
    try {
      // Per-container data attributes
      const centerAttr = container.getAttribute("data-center");
      const zoomAttr = container.getAttribute("data-zoom");

      const attrCenter = centerAttr
        ? (JSON.parse(centerAttr) as [number, number])
        : undefined;
      const attrZoom = zoomAttr ? parseFloat(zoomAttr) : undefined;

      // Precedence: URL > data attributes > defaults
      const center = urlCenter ?? urlCenter2 ?? attrCenter;
      const zoom = urlZoom ?? attrZoom;

      renderIntoShadow(container, center, zoom).catch((error) => {
        console.error("Failed to initialize map:", error);
      });
    } catch (error) {
      console.error("Failed to initialize map:", error);
    }
  });
}

onDomReady(initializeMaps);

export type { LocationsMapProps, LocationsMapConfig } from "./types";
